import { Cancel, Save } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Grid,
  Switch,
  TextField
} from '@mui/material';

import { Field, Formik } from 'formik';
import { pick } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { useJobTypeQuery, useJobTypesMutation } from 'api/hooks/jobmarket/jobtypes';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

interface Props {
  closeHandler: Function;
  typeId?: number;
  newType?: boolean;
}

export default function TypeDetail(props: Props) {
  const { t } = useTranslation();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { create, update } = useJobTypesMutation();

  const { data, isError, isLoading } = useJobTypeQuery(props.typeId);

  const handleCancelClick = dirty => {
    if (dirty) {
      setCancelModalOpen(true);
    } else {
      props.closeHandler();
    }
  };

  const handleSaveClick = async values => {
    try {
      if (props.newType) {
        await create.mutateAsync(values);
      } else {
        if (!props.typeId) {
          return;
        }
        await update.mutateAsync({
          id: props.typeId,
          data: pick(values, 'name', 'paid')
        });
      }
      props.closeHandler();
      dispatch(
        AlertActions.success(
          t(translations.pages.manageJobmarketSettings.alerts.saveTypeSuccessful())
        )
      );
    } catch (err) {
      dispatch(
        AlertActions.error(t(translations.pages.manageJobmarketSettings.alerts.saveTypeFailed()))
      );
    }
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  const typeData = data || {
    name: '',
    paid: false
  };

  if (isError) {
    dispatch(
      AlertActions.error(`${t(translations.pages.manageJobmarketSettings.alerts.errorLoading())}`)
    );
    navigate('/', { replace: true });
    return;
  }

  return (
    <Formik
      initialValues={typeData}
      validationSchema={yup.object().shape({
        name: yup.string().required(t(translations.validation.required())),
        paid: yup.bool().required(t(translations.validation.required()))
      })}
      onSubmit={handleSaveClick}
    >
      {({ dirty, handleSubmit, errors }) => {
        return (
          <form onSubmit={handleSubmit}>
            <ConfirmationDialog
              open={cancelModalOpen}
              title={t(translations.common.confirmCancel())}
              onClose={() => {
                setCancelModalOpen(false);
              }}
              onConfirm={() => {
                setCancelModalOpen(false);
                props.closeHandler();
              }}
            >
              <div>{t(translations.pages.manageJobmarketSettings.cancelConfirm.message())}</div>
            </ConfirmationDialog>
            <Grid container gap={1}>
              <Field
                as={TextField}
                name="name"
                error={errors.name}
                helperText={errors.name}
                required
                label={t(translations.common.name())}
                fullWidth
                size="small"
              />
              <Field name="paid" className="type-detail-modal__body__field">
                {({ form, field }) => (
                  <>
                    <FormControlLabel
                      label={t(translations.pages.manageJobmarketSettings.paid()) as string}
                      control={
                        <Switch
                          checked={form.values.paid}
                          onChange={field.onChange(field.name)}
                          color="primary"
                        />
                      }
                    />
                    {form.errors.paid && <FormHelperText>{form.errors.paid}</FormHelperText>}
                  </>
                )}
              </Field>
              <Grid container alignContent={'flex-end'} justifyContent={'flex-end'} gap={2}>
                <Button
                  color="error"
                  startIcon={<Cancel />}
                  size="small"
                  onClick={() => {
                    handleCancelClick(dirty);
                  }}
                >
                  {t(translations.common.cancel())}
                </Button>
                <Button type="submit" startIcon={<Save />} variant="outlined" size="small">
                  {t(translations.common.save())}
                </Button>
              </Grid>
            </Grid>
          </form>
        );
      }}
    </Formik>
  );
}
